import React, { useState, useCallback, useMemo, useContext, useEffect, useRef } from 'react';
import {
  SignupWizardContext,
  SignupWizardState,
  SignupTier,
  SignupStep,
  SignupStepName,
  MfaMethod,
} from './complexContexts';
import type { BillingPeriod } from '../utils/paymentTiers';
import { abandonSignup } from '../utils/signupApi';
import { logger } from '../utils/logger';

const STEP_NAMES: Record<SignupStep, SignupStepName> = {
  1: 'contact',
  2: 'verify-email',
  3: 'select-plan',
  4: 'account-details',
  5: 'payment',
  6: 'password',
  7: 'mfa',
};

const INITIAL_STATE: SignupWizardState = {
  step: 1,
  signupToken: null,
  cognitoUsername: null,
  email: '',
  firstName: '',
  lastName: '',
  displayName: '',
  initials: '',
  organization: undefined,
  emailVerified: false,
  selectedTier: null,
  billingPeriod: 'monthly',
  paymentComplete: false,
  passwordSet: false,
  mfaConfigured: false,
  mfaMethod: null,
  isLoading: false,
  error: null,
};

// Storage configuration
const STORAGE_KEY = 'signup_wizard_state';
const PERSISTENCE_EXPIRY_MS = 60 * 60 * 1000; // 1 hour
const DEBOUNCE_MS = 300;

interface PersistedState extends SignupWizardState {
  persistedAt: number;
}

/**
 * Simple encoding to prevent casual inspection of sensitive data in localStorage.
 * This is NOT encryption - it's obfuscation for basic privacy.
 *
 * Why this is acceptable for our use case:
 * 1. The signup token is short-lived (1 hour expiry enforced by backend)
 * 2. Token is single-use - consumed when signup completes
 * 3. State auto-expires and is cleaned up on successful signup
 * 4. XSS protection comes from React's automatic escaping (no dangerouslySetInnerHTML)
 * 5. sessionStorage would lose state during Stripe payment redirect (different page/domain)
 *
 * For sensitive credentials that persist longer, use Web Crypto API instead.
 */
function encodeState(state: PersistedState): string {
  return btoa(JSON.stringify(state));
}

function decodeState(encoded: string): PersistedState | null {
  try {
    return JSON.parse(atob(encoded));
  } catch (error) {
    logger.debug('Failed to decode signup wizard state', error);
    return null;
  }
}

function loadPersistedState(): SignupWizardState | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const persisted = decodeState(stored);
    if (!persisted) return null;

    // Check if expired
    if (Date.now() - persisted.persistedAt > PERSISTENCE_EXPIRY_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }

    // Return state without persistedAt, merging with INITIAL_STATE to handle newly-added fields
    const { persistedAt: _, ...restoredState } = persisted;
    let state: SignupWizardState = { ...INITIAL_STATE, ...restoredState };

    // Fix for stuck payment step: if user abandoned Stripe payment flow
    // (step 5 with paymentComplete: false), reset to step 4 so they can retry
    if (state.step === 5 && !state.paymentComplete) {
      state = { ...state, step: 4 };
    }

    // Reset transient UI state that shouldn't persist across sessions
    // (loading indicators, errors should start fresh on page load)
    state = { ...state, isLoading: false, error: null };

    return state;
  } catch (error) {
    // If anything fails, start fresh
    logger.debug('Failed to load persisted signup wizard state', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

function savePersistedState(state: SignupWizardState): void {
  try {
    const persisted: PersistedState = {
      ...state,
      persistedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, encodeState(persisted));
  } catch (error) {
    logger.debug('Failed to save signup wizard state to localStorage', error);
  }
}

function clearPersistedState(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    logger.debug('Failed to clear signup wizard state from localStorage', error);
  }
}

export const SignupWizardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize state from localStorage or use initial state
  const [state, setState] = useState<SignupWizardState>(() => {
    const persisted = loadPersistedState();
    return persisted || INITIAL_STATE;
  });

  // Debounce timer ref
  const persistenceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Persist state to localStorage when it changes (debounced)
  useEffect(() => {
    // Clear any pending timer
    if (persistenceTimerRef.current) {
      clearTimeout(persistenceTimerRef.current);
    }

    // Don't persist initial state or if no meaningful data
    if (!state.email && !state.signupToken) {
      return;
    }

    // Debounce the persistence
    persistenceTimerRef.current = setTimeout(() => {
      savePersistedState(state);
    }, DEBOUNCE_MS);

    return () => {
      if (persistenceTimerRef.current) {
        clearTimeout(persistenceTimerRef.current);
      }
    };
  }, [state]);

  // Flush pending persistence on page unload to prevent data loss
  useEffect(() => {
    const handleBeforeUnload = () => {
      // If there's a pending debounced save, flush it immediately
      if (persistenceTimerRef.current) {
        clearTimeout(persistenceTimerRef.current);
        persistenceTimerRef.current = null;
        // Only persist if we have meaningful data
        if (state.email || state.signupToken) {
          savePersistedState(state);
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state]);

  const setStep = useCallback((step: SignupStep) => {
    setState((prev) => ({ ...prev, step }));
  }, []);

  const setContactInfo = useCallback(
    (info: { email?: string; firstName?: string; lastName?: string; displayName?: string; initials?: string; organization?: string; businessName?: string; businessType?: string }) => {
      setState((prev) => ({
        ...prev,
        ...(info.email !== undefined && { email: info.email }),
        ...(info.firstName !== undefined && { firstName: info.firstName }),
        ...(info.lastName !== undefined && { lastName: info.lastName }),
        ...(info.displayName !== undefined && { displayName: info.displayName }),
        ...(info.initials !== undefined && { initials: info.initials }),
        ...(info.organization !== undefined && { organization: info.organization }),
        ...(info.businessName !== undefined && { businessName: info.businessName }),
        ...(info.businessType !== undefined && { businessType: info.businessType }),
      }));
    },
    []
  );

  const setSignupToken = useCallback((token: string | null) => {
    setState((prev) => ({ ...prev, signupToken: token }));
  }, []);

  const setCognitoUsername = useCallback((username: string | null) => {
    setState((prev) => ({ ...prev, cognitoUsername: username }));
  }, []);

  const setEmailVerified = useCallback((verified: boolean) => {
    setState((prev) => ({ ...prev, emailVerified: verified }));
  }, []);

  const setSelectedTier = useCallback((tier: SignupTier) => {
    setState((prev) => ({ ...prev, selectedTier: tier }));
  }, []);

  const setBillingPeriod = useCallback((period: BillingPeriod) => {
    setState((prev) => ({ ...prev, billingPeriod: period }));
  }, []);

  const setPaymentComplete = useCallback((complete: boolean) => {
    setState((prev) => ({ ...prev, paymentComplete: complete }));
  }, []);

  const setPasswordSet = useCallback((set: boolean) => {
    setState((prev) => ({ ...prev, passwordSet: set }));
  }, []);

  const setMfaConfigured = useCallback((configured: boolean) => {
    setState((prev) => ({ ...prev, mfaConfigured: configured }));
  }, []);

  const setMfaMethod = useCallback((method: MfaMethod) => {
    setState((prev) => ({ ...prev, mfaMethod: method }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState((prev) => ({ ...prev, error }));
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setState((prev) => ({ ...prev, isLoading: loading }));
  }, []);

  const resetWizard = useCallback(() => {
    setState(INITIAL_STATE);
    clearPersistedState();
  }, []);

  /**
   * Abandon an in-progress signup session and reset to initial state.
   *
   * This calls the backend to clean up the Cognito user and DynamoDB record,
   * then resets local state. Use this when users want to start fresh.
   *
   * @returns true if abandonment was successful, false otherwise
   */
  const abandonAndReset = useCallback(async (): Promise<boolean> => {
    const { signupToken } = state;

    // If no signup token, just reset locally
    if (!signupToken) {
      resetWizard();
      return true;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await abandonSignup(signupToken);

      if (result.success) {
        resetWizard();
        return true;
      } else {
        // Even if backend cleanup fails, we should still reset local state
        // (the backend cleanup Lambda will eventually clean up orphaned records)
        resetWizard();
        return true;
      }
    } catch (error) {
      // Network error - still reset local state
      logger.warn('Failed to abandon signup on backend, resetting local state', error);
      resetWizard();
      return true;
    } finally {
      setLoading(false);
    }
  }, [state, resetWizard, setLoading, setError]);

  /**
   * Indicates if this session was restored from localStorage.
   * True when step > 1 or signupToken exists (user has started signup before).
   */
  const isResumingSession = useMemo(
    () => state.step > 1 || state.signupToken !== null,
    [state.step, state.signupToken]
  );

  const isPaidTier = useMemo(
    () => state.selectedTier === 'creator' || state.selectedTier === 'commercial',
    [state.selectedTier]
  );

  const requiresMfa = useMemo(
    () => state.selectedTier === 'creator' || state.selectedTier === 'commercial' || state.selectedTier === 'delegate' || state.selectedTier === 'viewer',
    [state.selectedTier]
  );

  const currentStepName = useMemo(() => STEP_NAMES[state.step], [state.step]);

  const canProceedToStep = useCallback(
    (step: SignupStep): boolean => {
      switch (step) {
        case 1:
          // Can always go back to step 1
          return true;

        case 2:
          // Need email and signupToken from step 1 initiation
          return Boolean(state.email && state.firstName && state.lastName && state.signupToken);

        case 3:
          // Need verified email
          return Boolean(
            state.email &&
              state.firstName &&
              state.lastName &&
              state.signupToken &&
              state.emailVerified
          );

        case 4:
          // Account details step - only for paid tiers
          // For viewer tier, this step is skipped
          if (state.selectedTier === 'viewer') {
            return false; // Not applicable - skip to step 6
          }
          return Boolean(
            state.email &&
              state.firstName &&
              state.lastName &&
              state.signupToken &&
              state.emailVerified &&
              state.selectedTier
          );

        case 5:
          // Payment step - only for paid tiers
          if (state.selectedTier === 'viewer' || state.selectedTier === 'delegate') {
            return false; // Not applicable - skip to step 6
          }
          return Boolean(
            state.email &&
              state.firstName &&
              state.lastName &&
              state.signupToken &&
              state.emailVerified &&
              state.selectedTier
          );

        case 6:
          // Password step
          // For viewer tier, can proceed after email verification (skip payment)
          if (state.selectedTier === 'viewer') {
            return Boolean(
              state.email &&
                state.firstName &&
                state.lastName &&
                state.signupToken &&
                state.emailVerified
            );
          }
          // For delegate tier, can proceed after email verification + tier selected (no payment)
          if (state.selectedTier === 'delegate') {
            return Boolean(
              state.email &&
                state.firstName &&
                state.lastName &&
                state.signupToken &&
                state.emailVerified &&
                state.selectedTier
            );
          }
          // For paid tiers, need payment complete
          return Boolean(
            state.email &&
              state.firstName &&
              state.lastName &&
              state.signupToken &&
              state.emailVerified &&
              state.paymentComplete
          );

        case 7:
          // MFA step - required for all tiers
          // For viewer tier, need password set (no payment required)
          if (state.selectedTier === 'viewer') {
            return Boolean(
              state.email &&
                state.firstName &&
                state.lastName &&
                state.signupToken &&
                state.emailVerified &&
                state.passwordSet
            );
          }
          // For delegate tier, need password set (no payment required)
          if (state.selectedTier === 'delegate') {
            return Boolean(
              state.email &&
                state.firstName &&
                state.lastName &&
                state.signupToken &&
                state.emailVerified &&
                state.passwordSet
            );
          }
          // For paid tiers, need password set + payment complete
          return Boolean(
            state.email &&
              state.firstName &&
              state.lastName &&
              state.signupToken &&
              state.emailVerified &&
              state.paymentComplete &&
              state.passwordSet
          );

        default:
          return false;
      }
    },
    [state]
  );

  const value = useMemo(
    () => ({
      state,
      setStep,
      setContactInfo,
      setSignupToken,
      setCognitoUsername,
      setEmailVerified,
      setSelectedTier,
      setBillingPeriod,
      setPaymentComplete,
      setPasswordSet,
      setMfaConfigured,
      setMfaMethod,
      setError,
      setLoading,
      resetWizard,
      abandonAndReset,
      canProceedToStep,
      isResumingSession,
      isPaidTier,
      requiresMfa,
      currentStepName,
    }),
    [
      state,
      setStep,
      setContactInfo,
      setSignupToken,
      setCognitoUsername,
      setEmailVerified,
      setSelectedTier,
      setBillingPeriod,
      setPaymentComplete,
      setPasswordSet,
      setMfaConfigured,
      setMfaMethod,
      setError,
      setLoading,
      resetWizard,
      abandonAndReset,
      canProceedToStep,
      isResumingSession,
      isPaidTier,
      requiresMfa,
      currentStepName,
    ]
  );

  return <SignupWizardContext.Provider value={value}>{children}</SignupWizardContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useSignupWizard = () => {
  const context = useContext(SignupWizardContext);
  if (context === undefined) {
    throw new Error('useSignupWizard must be used within a SignupWizardProvider');
  }
  return context;
};
